import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, Switch, Tooltip } from "@mui/material";
import { IFlightCrewAcceptanceRequestResult } from "@vmosolutions/oc-interfaces";
import moment from "moment-timezone";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { useCancellablePromise } from "src/API/useCancellablePromise";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { PositionBadge } from "../CrewSchedule/PositionBadge";
import LoadingWrapper from "../LoadingWrapper/LoadingWrapper";

interface IDocumentAcknowledgementsProps {
  flightId: string;
  id: string;
}
export function DocumentAcknowledgements(props: IDocumentAcknowledgementsProps): JSX.Element {
  const { flightId, id } = props;
  const [flightReleaseState, setFlightReleaseState] = useState<IFlightCrewAcceptanceRequestResult | null>();
  const contextMenuOpen = useSelector((state: IRootState) => state.timelineFlights.isFlightContextOpen);
  const [loadingContent, setLoadingContent] = useState<boolean>(true);
  const [showAll, setShowAll] = useState<boolean>(false);
  const { cancellablePromise } = useCancellablePromise();

  const getFlightReleaseState = useCallback(async (): Promise<void> => {
    setFlightReleaseState(null);
    const res = await AmplifyAPIWrapper.Get<any>(`/flights/release/state/${flightId}`, undefined, cancellablePromise);
    if (res && res.hasOwnProperty("_id")) {
      setFlightReleaseState(res);
    }
    setLoadingContent(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightId]);

  useEffect(() => {
    setLoadingContent(true);
    setFlightReleaseState(null);
    getFlightReleaseState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (contextMenuOpen[id]) {
      setLoadingContent(true);
      setFlightReleaseState(null);
      getFlightReleaseState();
    }
  }, [flightId, getFlightReleaseState, contextMenuOpen, id]);

  const uniqueContentTypes = useMemo(() => {
    if (flightReleaseState) {
      return [...new Set(flightReleaseState.contents.map((item) => item.contentType.title))];
    }
    return [];
  }, [flightReleaseState]);

  const selectNewContentByType = useCallback((): any[] => {
    const recentContents: any[] = [];
    if (!flightReleaseState) {
      return [];
    }
    uniqueContentTypes.map((contentType, contentTypeKey) => {
      const efbFiles = flightReleaseState.contents
        .filter((content) => {
          return content.hasOwnProperty("efb") && content.contentType.title === contentType;
        })
        .sort((a, b) => {
          return a.displayTitle > b.displayTitle ? -1 : 1;
        });
      if (efbFiles && efbFiles.length > 0) {
        recentContents.push(efbFiles[0]);
      }
    });

    return recentContents;
  }, [flightReleaseState, uniqueContentTypes]);

  const documentsToShow = useCallback(() => {
    if (!flightReleaseState) {
      return [];
    }
    if (!showAll) {
      return flightReleaseState.contents.sort((a, b) => {
        return a.createdAt > b.createdAt ? -1 : 1;
      });
    } else {
      return selectNewContentByType().sort((a, b) => {
        return a.createdAt > b.createdAt ? -1 : 1;
      });
    }
  }, [flightReleaseState, showAll, selectNewContentByType]);

  return (
    <>
      {" "}
      {documentsToShow().length > 0 && (
        <Accordion
          defaultExpanded={true}
          sx={{
            width: "100%",
            m: 0,
            p: 0,
            "&.Mui-expanded": {
              marginTop: "0 !important",
              marginBottom: "0 !important",
              padding: "0 !important",
              "& .MuiAccordionSummary-root": {
                minHeight: "unset",
              },
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ my: 0 }} />}
            aria-controls={"panel-docack--content"}
            id={"panel-docAck-header"}
            sx={(theme: any) => ({
              display: "flex",
              justifyContent: "space-between",
              "& .MuiAccordionSummary-content": {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              },
            })}
          >
            Document Ack.
            <Box sx={{ fontSize: "0.8em" }}>
              Show All
              <Switch
                checked={!showAll}
                size="small"
                onClick={(e: any) => {
                  e.stopPropagation();
                  setShowAll(!showAll);
                }}
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 1, pl: 2, maxHeight: showAll ? "unset" : 150, overflowY: "auto" }}>
            {loadingContent && (
              <Box sx={{ py: 6, px: 2, display: "flex", justifyContent: "center", width: "100%" }}>
                <LoadingWrapper message="Loading Documents..." />
              </Box>
            )}
            {!loadingContent && flightReleaseState && (
              <Grid container spacing={0}>
                {documentsToShow().map((content: any, key: number) => {
                  return (
                    <Grid item xs={12} key={key}>
                      <Grid container spacing={0}>
                        <Grid
                          item
                          xs={12}
                          sx={(theme: any) => ({
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "2px",
                            "&:hover": { backgroundColor: theme.palette.action.hover },
                          })}
                        >
                          <Grid item xs sx={{ fontSize: "0.8em" }}>
                            {content.displayTitle}
                          </Grid>
                          <Grid item xs="auto" sx={{ display: "flex", justifyContent: "end" }}>
                            {flightReleaseState.crew.map((crew: any, crewKey: number) => {
                              const accepted = content.accepted.find((acceptee: any) => {
                                return acceptee.acceptedBy.id === crew._id;
                              });
                              if (!accepted) {
                                return (
                                  <Box sx={{ mr: "4px" }} key={crewKey}>
                                    <PositionBadge
                                      positionTitle={crew.position}
                                      filled={content.accepted.length > 0 ? true : false}
                                      error={content.accepted.length === 0 ? "error" : ""}
                                    />
                                  </Box>
                                );
                              } else {
                                return (
                                  <Tooltip
                                    key={crewKey}
                                    title={`${accepted.acceptedBy.firstName} ${accepted.acceptedBy.lastName} (${moment(
                                      accepted.acceptTimeServer
                                    )
                                      .utc()
                                      .format("MMM DD, YYYY, HH:mm")})`}
                                  >
                                    <Box sx={{ mr: "4px" }}>
                                      <PositionBadge
                                        positionTitle={accepted.acceptedBy.crewPosOnFlt}
                                        filled={accepted.acceptedBy.id === crew._id ? true : false}
                                      />
                                    </Box>
                                  </Tooltip>
                                );
                              }
                            })}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
}
